import React, { useEffect, useState, useRef, useCallback } from 'react';
import { FiUsers, FiUserPlus, FiMail, FiUploadCloud, FiAlertTriangle } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import CandidateItem from './candidateItem';
import { RotateCcw,CircleX } from 'lucide-react';
import { RecruiterApi } from '../../../supports/recruiterApi';
import Loader from '../../../common/loader';
import { generateRequestId } from '../../../common/common';
import { resumeUpload } from '../../../supports/resumeUpload';
import webSocketParserService from '../../../services/webSocketService';
import unifiedAuthManager from '../../../services/unifiedAuthManager';
import { useToast } from '../../../common/toastSystem';
import EmailModal from '../../../common/emailModal';
import PlacedCandidateItem from './placedCandidateItem';
// import MultiSelectCandidateDropdown from '../../../common/multiSelectCandidate';
import useDropdownData from '../../candidateProfiler/profilerEditingPage/useDropDownData';
const PAGE_SIZE = 10;
const MAX_RESUME_COUNT = 5;
const MAX_RESUME_SIZE_BYTES = 2 * 1024 * 1024;
const ALLOWED_FILE_EXTENSIONS = new Set(['pdf', 'docx', 'txt']);

const normalizeFileNameExtension = (fileName = '') => {
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex <= 0 || lastDotIndex === fileName.length - 1) {
    return fileName;
  }
  const baseName = fileName.slice(0, lastDotIndex);
  const extension = fileName.slice(lastDotIndex + 1).toLowerCase();
  return `${baseName}.${extension}`;
};

const normalizeComparableFileName = (fileName = '') =>
  normalizeFileNameExtension(fileName).trim().toLowerCase();

const normalizeFileForUpload = (file) => {
  if (!file?.name) return file;
  const normalizedName = normalizeFileNameExtension(file.name);
  if (!normalizedName || normalizedName === file.name) {
    return file;
  }
  return new File([file], normalizedName, {
    type: file.type,
    lastModified: file.lastModified,
  });
};

const readFileAsArrayBuffer = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`Unable to read ${file.name}.`));
    reader.readAsArrayBuffer(file);
  });

const startsWithBytes = (arr, bytes) =>
  bytes.every((byte, index) => arr[index] === byte);

const decodeBytes = (arr, start = 0, end = arr.length) =>
  new TextDecoder('latin1').decode(arr.slice(start, end));

const hasPdfEncryptionMarker = (arr) => {
  const probeLength = Math.min(arr.length, 300000);
  const text = decodeBytes(arr, 0, probeLength);
  return /\/Encrypt\b/i.test(text);
};

const isLikelyCorruptedPdf = (arr) => {
  if (arr.length < 8) return true;
  const header = decodeBytes(arr, 0, 8);
  if (!header.startsWith('%PDF-')) return true;
  const trailerStart = Math.max(0, arr.length - 2048);
  const trailer = decodeBytes(arr, trailerStart, arr.length);
  return !trailer.includes('%%EOF');
};

const hasOleSignature = (arr) =>
  startsWithBytes(arr, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]);

const isLikelyDocxZip = (arr) => startsWithBytes(arr, [0x50, 0x4b, 0x03, 0x04]);

const isLikelyCorruptedDocx = (arr) => {
  if (!isLikelyDocxZip(arr)) return true;
  const text = decodeBytes(arr);
  return !text.includes('[Content_Types].xml') || !text.includes('word/document.xml');
};

const isLikelyCorruptedTextFile = (arr) => {
  if (arr.length === 0) return true;
  let invalid = 0;
  for (let i = 0; i < arr.length; i += 1) {
    const byte = arr[i];
    const isAllowedControl = byte === 9 || byte === 10 || byte === 13;
    const isPrintable = byte >= 32 && byte <= 126;
    const isExtended = byte >= 160;
    if (!isAllowedControl && !isPrintable && !isExtended) {
      invalid += 1;
    }
  }
  return invalid / arr.length > 0.05;
};

const CandidateList = ({ candidates: searchCandidates, pageType, query, searchType, job_id, isTyping=false, onLoadMore,onCandidateUpdateFilter, searchHasMore, setSearchHasMore }) => {
  const [candidates, setCandidates] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchPage, setSearchPage] = useState(0);
  // const [searchHasMore, setSearchHasMore] = useState(true);
  const user = unifiedAuthManager.getUnifiedUser();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [failedFiles, setFailedFiles] = useState([]);
  const [skippedFiles, setSkippedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [showResumeStatusModal, setShowResumeStatusModal] = useState(false);
  const [resumeStatusData, setResumeStatusData] = useState([]);
  const [currentUploadFiles, setCurrentUploadFiles] = useState([]);
  const [websocketResult, setWebsocketResult] = useState([]);
  const [postProcessTable, setPostProcessTable] = useState([]);
  const [postProcessError, setPostProcessError] = useState('');
  const [showOnlyPostProcessTable, setShowOnlyPostProcessTable] = useState(false);
  const scrollContainerRef = useRef(null);
  const isFetchingRef = useRef(false);
  const isCallingPostProcessRef = useRef(false);
  const [selectedIdsForEmail, setSelectedIdsForEmail] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const toast = useToast();
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [activeTab, setActiveTab] = useState("Candidates");
  const [candidateCount, setCandidateCount] = useState(0);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [placedCount, setPlacedCount] = useState(0);
  const [contactedCount, setContactedCount] = useState(0);
  const [showTabDropdown, setShowTabDropdown] = useState(false);
  const tabDropdownRef = useRef(null);
  const [selectedSectors, setSelectedSectors] = useState([]);
  const {sectors} = useDropdownData();


  const fetchCandidates = useCallback(async (pageNum = 0, tab) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setLoading(true);
    try {
        let payload = {
        user_payload: { request_id: generateRequestId() },
        page: pageNum,
        count: PAGE_SIZE,
      }
      if(tab === 'UploadedByMe'){
        payload.flag = 'uploadedbyme'
      }

      let response;
      if (tab === 'ContactedByMe') {
        payload.user_payload = {
          ...payload.user_payload,
          user_id: user.id,
          email: user.email,
          tenant: "GBB"
        };
        response = await RecruiterApi.getContactedByMe(payload);
      } else {
        response = await RecruiterApi.getCandidates(payload);
      }

      if (response.success) {
        let newCandidates = [];
        let total = 0;
        
        if (tab === 'ContactedByMe') {
          newCandidates = response.data?.candidates || response.data || [];
          if (!Array.isArray(newCandidates)) {
            newCandidates = [];
          }
          total = response.data?.total_count ?? newCandidates.length;
        } else {
          newCandidates = response.data.candidates || [];
          total = response.data.total_count || 0;
        }
        
        if (pageNum === 0) {
          setCandidates(newCandidates);
        } else {
          setCandidates(prev => [...prev, ...newCandidates]);
        }
        
        setTotalCount(total);
        if (tab === 'ContactedByMe') setContactedCount(total);
        
        // Disable pagination for ContactedByMe as the API returns all at once currently
        setHasMore(tab === 'ContactedByMe' ? false : newCandidates.length === PAGE_SIZE);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      setHasMore(false);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [user.id, user.email]);

  useEffect(() => {
  
    if (activeTab === "Placed") {
      fetchPlacedCandidates();
      return;
    }
    if (searchCandidates && searchCandidates.loading) {
      setCandidates([]);
      setLoading(true);
      return;
    }

    if (Array.isArray(searchCandidates?.candidates) && activeTab === "Candidates") {
      console.log("search candidates updated", searchCandidates);
      setLoading(true);
      setCandidates([]);
      setTimeout(() => {
        setTotalCount(
          searchCandidates.query?.total_count || searchCandidates.candidates.length
        );
        setCandidates(searchCandidates.candidates);
        setShowSearchResults(true);
        setSearchPage(0);
        setSearchHasMore(true);
        setLoading(false);
      }, 100);
    } else {
      // fallback to default API
      setShowSearchResults(false);
      setCandidates([]);
      setPage(0);
      setHasMore(true);
      fetchCandidates(0,activeTab);
    }
  }, [searchCandidates, activeTab]);


  useEffect(() => {
  const handleClickOutside = (event) => {
    if (tabDropdownRef.current && !tabDropdownRef.current.contains(event.target)) {
      setShowTabDropdown(false);
    }
  };

  if (showTabDropdown) {
    document.addEventListener('mousedown', handleClickOutside);
  }

  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };
}, [showTabDropdown]);

  const fetchPlacedCandidates = async () => {
    try {
      if (loading) return
      setLoading(true);
      setCandidates([])
      let payload = {
        user_payload: {
          user_id: user.id,
          request_id: generateRequestId(),
          email: user.email,
          tenant: "GBB"
        },
      }
      const response = await RecruiterApi.getPlacedCandidates(payload)
      console.log(response);
      if (response.success) {
        const newCandidates = response.data || [];
        setTotalCount(response.data.length || 0);
        setCandidates(newCandidates);
      } else {
        toast.warning('Failed to fetch placed candidates')
      }
    } catch (err) {
      toast.warning('Failed to fetch placed candidates')
    } finally {
      setLoading(false);
    }
  };


  
  const fetchUploadedByMe = async () => {
    try {
      if (loading) return
      setLoading(true);
      setCandidates([])
      let payload = {
        user_payload: {
          user_id: user.id,
          request_id: generateRequestId(),
          email: user.email,
          tenant: "GBB"
        },
      }
      const response = await RecruiterApi.fetchCandidatesUploadedByMe(payload)
      console.log(response);
      if (response.success) {
        const newCandidates = response.data || [];
        setTotalCount(response.data.length || 0);
        setCandidates(newCandidates);
      } else {
        toast.warning('Failed to fetch candidates uploaded by me')
      }
    } catch (err) {
      toast.warning('Failed to fetch candidates uploaded by me')
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = useCallback(() => {
    if(activeTab === 'Placed' || activeTab === 'ContactedByMe')return;
    const container = scrollContainerRef.current;
    if (!container) return;
    
    if (showSearchResults) {
      if (onLoadMore && searchHasMore && !loading && !isFetchingRef.current) {
        if (container.scrollHeight - container.scrollTop - container.clientHeight < 200) {
          isFetchingRef.current = true;
          
          const newPage = searchPage + 1;
          setLoading(true);
          
          onLoadMore(newPage)
            .then(newCandidates => {
              if (newCandidates && newCandidates.length > 0) {
                setCandidates(prev => [...prev, ...newCandidates]);
                setSearchPage(newPage);
                // setSearchHasMore(newCandidates.length === PAGE_SIZE);
              } else {
                setSearchHasMore(false);
              }
            })
            .catch(() => {
              setSearchHasMore(false);
            })
            .finally(() => {
              setLoading(false);
              isFetchingRef.current = false;
            });
        }
      }
    } else {
      if (
        container.scrollHeight - container.scrollTop - container.clientHeight < 200 &&
        !loading &&
        hasMore &&
        !isFetchingRef.current
      ) {
        setPage(prev => prev + 1);
      }
    }
  }, [showSearchResults, onLoadMore, searchHasMore, loading, searchPage, hasMore]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    if (showSearchResults || page === 0) return;
    fetchCandidates(page, activeTab);
  }, [page, fetchCandidates, showSearchResults]);

  const iconButtonStyles = "h-8 w-8 flex items-center justify-center border border-gray-200 rounded-full text-gray-666 hover:bg-gray-100";

  const handleAddCandidateClick = () => {
    setShowUploadModal(true);
    setUploadError('');
    setUploadSuccess('');
    setSelectedFiles([]);
    setFailedFiles([]);
    setSkippedFiles([]);
    setWebsocketResult([]);
    setPostProcessTable([]);
    setPostProcessError('');
    setShowOnlyPostProcessTable(false);
  };

  const validateAndPartitionFiles = async (files) => {
    const validFiles = [];
    const invalidFiles = [];

    for (const incomingFile of files) {
      const file = normalizeFileForUpload(incomingFile);
      const fileName = file?.name || incomingFile?.name || 'Unnamed file';
      let reason = '';

      if (!file) {
        reason = 'File is not readable.';
      } else if (file.size === 0) {
        reason = 'Empty file. Please upload a non-empty file.';
      } else if (file.size > MAX_RESUME_SIZE_BYTES) {
        reason = 'File not uploaded: Exceeds the 2 MB size limit.';
      } else {
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        if (!ALLOWED_FILE_EXTENSIONS.has(ext)) {
          reason = 'Only PDF, DOCX files are allowed.';
        } else {
          try {
            const buffer = await readFileAsArrayBuffer(file);
            const bytes = new Uint8Array(buffer);

            if (bytes.length === 0) {
              reason = 'Empty file. Please upload a non-empty file.';
            } else if (ext === 'pdf') {
              if (hasPdfEncryptionMarker(bytes)) {
                reason = 'Password-protected PDF is not allowed.';
              } else if (isLikelyCorruptedPdf(bytes)) {
                reason = 'Corrupted PDF file is not allowed.';
              }
            } else if (ext === 'docx') {
              if (hasOleSignature(bytes)) {
                reason = 'Password-protected DOCX is not allowed.';
              } else if (isLikelyCorruptedDocx(bytes)) {
                reason = 'Corrupted DOCX file is not allowed.';
              }
            } else if (ext === 'txt' && isLikelyCorruptedTextFile(bytes)) {
              reason = 'Corrupted text file is not allowed.';
            }
          } catch (error) {
            reason = 'Unable to read file.';
          }
        }
      }

      if (reason) {
        invalidFiles.push({ name: fileName, reason });
      } else {
        validFiles.push(file);
      }
    }

    return { validFiles, invalidFiles };
  };

  const appendFiles = async (incomingFiles) => {
    let newFiles;
    if (incomingFiles.length === 0) return;

    if (selectedFiles.length === 0) {
      newFiles = [];
      for (let file of incomingFiles) {
        const normalizedName = normalizeFileNameExtension(file.name || '');
        if (!newFiles.some(f => normalizeFileNameExtension(f.name || '') === normalizedName && f.size === file.size)) {
          newFiles.push(file);
        }
      }
    } else {
      newFiles = [...selectedFiles];
      for (let file of incomingFiles) {
        const normalizedName = normalizeFileNameExtension(file.name || '');
        if (!newFiles.some(f => normalizeFileNameExtension(f.name || '') === normalizedName && f.size === file.size)) {
          newFiles.push(file);
        }
      }
    }

    if (newFiles.length > MAX_RESUME_COUNT) {
      setUploadError('More than 5 resumes selected. Please upload a maximum of 5 resumes at a time.');
      return;
    }

    const { validFiles, invalidFiles } = await validateAndPartitionFiles(newFiles);
    setSelectedFiles(validFiles);
    setSkippedFiles(invalidFiles);
    setUploadError('');
  };

  const handleFileChange = async (e) => {
    setUploadError('');
    setUploadSuccess('');
    setFailedFiles([]);
    setSkippedFiles([]);
    setWebsocketResult([]);
    setPostProcessTable([]);
    setPostProcessError('');
    setShowOnlyPostProcessTable(false);
    const files = Array.from(e.target.files);
    await appendFiles(files);
    e.target.value = '';
  };

  const handleRemoveSelectedFile = (fileToRemove, removeIndex) => {
    setUploadError('');
    setUploadSuccess('');
    setFailedFiles([]);
    setSelectedFiles((prevFiles) =>
      prevFiles.filter(
        (file, idx) =>
          !(
            idx === removeIndex &&
            file.name === fileToRemove.name &&
            file.size === fileToRemove.size
          )
      )
    );
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading) setDragActive(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading) setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading) setDragActive(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setUploadError('');
    setUploadSuccess('');
    setFailedFiles([]);
    setSkippedFiles([]);
    setWebsocketResult([]);
    setPostProcessTable([]);
    setPostProcessError('');
    setShowOnlyPostProcessTable(false);
    if (uploading) return;
    const files = Array.from(e.dataTransfer.files);
    await appendFiles(files);
  };

  const callBulkUploadPostProcess = async (candidateIds, fileIdFileNameMap) => {
    if (isCallingPostProcessRef.current) return;
    isCallingPostProcessRef.current = true;
    try {
      setPostProcessError('');
      const res = await RecruiterApi.getResumeStatus({
        resume_ids: candidateIds,
        user_payload: { request_id: generateRequestId() }
      });
      console.log(res)
      if (res && res.success) {
        const bulkStatusUpdates = Array.isArray(res.data)
          ? res.data
              .filter(
                (item) =>
                  item?.status === 'Success' &&
                  !item?.error &&
                  Number.isFinite(Number(item?.resume_id)) &&
                  Number.isFinite(Number(item?.candidate_id))
              )
              .map((item) => ({
                resume_id: Number(item.resume_id),
                candidate_id: Number(item.candidate_id)
              }))
          : [];

        if (bulkStatusUpdates.length > 0) {
          await RecruiterApi.updateResumeStatusBulk({
            updates: bulkStatusUpdates,
            user_payload: { request_id: generateRequestId() }
          });
        }

        if (Array.isArray(res.data) && res.data.length == 0) {
          toast.success("File uploaded successfully.")
          setShowResumeStatusModal(false)
          return
        }
        const resultRows = res.data.map((item) => {
          let status = 'Complete';
          if (typeof item.error === "string" && item.error.length > 0) {
            status = 'Failed';
          }
          else if (item.error !== null && item.error !== undefined && item.error !== "") {
            status = 'Failed';
          }
          const missingFieldsArr =
            Array.isArray(item.missing_fields) && item.missing_fields.length > 0
              ? item.missing_fields
              : [];
          const emptyFieldsArr =
            Array.isArray(item.empty_fields) && item.empty_fields.length > 0
              ? item.empty_fields
              : [];
          const fields = Array.from(new Set([...missingFieldsArr, ...emptyFieldsArr]));
          return {
            id: item.resume_id || item.candidate_id || '',
            fileName: item.file_name || fileIdFileNameMap[item.resume_id] || '',
            status,
            fields,
            error: item.error || "",
            message: item.message || "",
          };
        });
        setPostProcessTable(resultRows);
        setShowOnlyPostProcessTable(true);
      } else {
        setShowResumeStatusModal(false)
        setPostProcessTable([]);
        toast.warning('Failed to get the progress of the files.Upload it again.');
      }
    } catch (err) {
      setShowResumeStatusModal(false)
      setPostProcessTable([]);
      toast.warning('Failed to get the progress of the files.Upload it again.');
    } finally {
      isCallingPostProcessRef.current = false;
    }
  };

  const handleUpload = async () => {
    setUploadError('');
    setUploadSuccess('');
    setFailedFiles([]);
    setWebsocketResult([]);
    setPostProcessTable([]);
    setPostProcessError('');
    setShowOnlyPostProcessTable(false);
    if (selectedFiles.length === 0) {
      setUploadError('Please select at least one resume to upload.');
      return;
    }
    // if (selectedSectors.length === 0) {
    //   setUploadError('Please select at least one sector.');
    //   return;
    // }
    const { validFiles, invalidFiles } = await validateAndPartitionFiles(selectedFiles);
    if (invalidFiles.length > 0) {
      setSkippedFiles(invalidFiles);
    }
    if (validFiles.length === 0) {
      setSelectedFiles([]);
      setUploadError('No valid resumes to upload.');
      return;
    }
    setSelectedFiles(validFiles);
    setUploading(true);
    try {
      const response = await resumeUpload.addResume({
        files: validFiles,
        user_payload: {
          request_id: generateRequestId(),
          user_id: user?.id
        }
      });
      let failed = [];
      if (response && response.success) {
        const backendSummary = Array.isArray(response?.data?.summary) ? response.data.summary : [];
        const summaryUsedIndex = new Set();
        const getSummaryForFile = (fileName) => {
          const normalizedInput = normalizeComparableFileName(fileName);
          let foundIndex = backendSummary.findIndex(
            (item, idx) => !summaryUsedIndex.has(idx) && item?.file_name === fileName
          );
          if (foundIndex < 0) {
            foundIndex = backendSummary.findIndex((item, idx) => {
              if (summaryUsedIndex.has(idx)) return false;
              return normalizeComparableFileName(item?.file_name || '') === normalizedInput;
            });
          }
          if (foundIndex < 0) {
            foundIndex = backendSummary.findIndex((item, idx) => !summaryUsedIndex.has(idx));
          }
          if (foundIndex >= 0) {
            summaryUsedIndex.add(foundIndex);
            return backendSummary[foundIndex];
          }
          return null;
        };

        let progressMap = [];
        const idToFileName = {};
        if (backendSummary.length > 0) {
          progressMap = validFiles.map((file, idx) => {
            const summaryItem = getSummaryForFile(file.name);
            const docId = summaryItem?.doc_id;
            const isValidDocId = docId !== null && docId !== undefined && docId !== '';
            const summaryStatus = String(summaryItem?.status || '').toUpperCase();
            const isFailedSummary = summaryStatus === 'FAILED' || summaryStatus === 'ERROR';
            const apiMessage = summaryItem?.message || (isValidDocId ? 'Starting upload.' : 'Upload failed.');
            if (isValidDocId) {
              idToFileName[docId] = file.name || "";
            }
            return {
              id: isValidDocId ? docId : `failed-${idx}-${file.name}`,
              fileName: file.name || "",
              status: isValidDocId && !isFailedSummary ? 'Connecting' : 'Failed',
              progress: isValidDocId ? 0 : 100,
              message: isFailedSummary ? apiMessage : 'Starting upload.',
              apiMessage: isFailedSummary ? apiMessage : '',
              canCancel: isValidDocId && !isFailedSummary,
            };
          });
        } else if (Array.isArray(response?.data?.doc_id)) {
          progressMap = response.data.doc_id.map((id, idx) => {
            idToFileName[id] = validFiles[idx]?.name || "";
            return {
              id,
              fileName: validFiles[idx]?.name || "",
              status: 'Connecting',
              progress: 0,
              message: 'Starting upload.',
              apiMessage: 'Starting upload.',
              canCancel: true,
            };
          });
        }
        uploadIdToFileNameRef.current = idToFileName;
        setResumeStatusData(progressMap);
        setShowResumeStatusModal(true);
        setWebsocketResult(progressMap);
        setShowUploadModal(false);
        setUploadError('');
        progressMap
          .filter((item) => item.canCancel)
          .forEach((item) => {
            webSocketParserService.connect(item.id);
          });
        failed = progressMap.filter((item) => item.status === 'Failed' || item.status === 'Error');
      } else {
        failed = [...validFiles];
      }
      if (failed.length === 0) {
        setUploadSuccess('Resumes uploaded successfully!');
        setUploadError('');
      } else {
       
        setFailedFiles(failed);
        setUploadSuccess('');
       
      }
    } catch (err) {
      setUploadError('Failed to upload resumes.');
    } finally {
      setUploading(false);
    }
  };

  const uploadIdToFileNameRef = useRef({});
  useEffect(() => {
    let isMounted = true;
    const getWsPayload = (eventData) =>
      eventData && typeof eventData.data === 'object' && eventData.data !== null
        ? eventData.data
        : eventData;
    const getWsId = (eventData) => {
      const payload = getWsPayload(eventData);
      return eventData?.id ?? payload?.id;
    };
    const handleProgress = (msg) => {
      const wsId = getWsId(msg);
      const payload = getWsPayload(msg);
      setResumeStatusData(prev =>
        prev.map(item =>
          String(item.id) === String(wsId)
            ? { ...item, status: 'In Progress', progress: payload?.percentage || 50, message: payload?.message || 'Resume is processing.' }
            : item
        )
      );
      setWebsocketResult(prev =>
        prev.map(item =>
          String(item.id) === String(wsId)
            ? { ...item, status: 'In Progress', progress: payload?.percentage || 50, message: payload?.message || 'Resume is processing.' }
            : item
        )
      );
    };

    const handleComplete = (msg) => {
      const wsId = getWsId(msg);
      const payload = getWsPayload(msg);
      setResumeStatusData(prev =>
        prev.map(item =>
          String(item.id) === String(wsId)
            ? { ...item, status: 'Complete', progress: 100, message: payload?.message || 'Resume processing completed.' }
            : item
        )
      );
      setWebsocketResult(prev => {
        const next = prev.map(item =>
          String(item.id) === String(wsId)
            ? { ...item, status: 'Complete', progress: 100, message: payload?.message || 'Resume processing completed.' }
            : item
        );
        maybeCallPostProcess(next);
        return next;
      });
      webSocketParserService.disconnect(wsId);
    };

    const handleSuccess = (msg) => {
      const wsId = getWsId(msg);
      const payload = getWsPayload(msg);
      setResumeStatusData(prev =>
        prev.map(item =>
          String(item.id) === String(wsId)
            ? { ...item, status: 'Success', progress: 100, message: payload?.message || 'Resume processing completed.' }
            : item
        )
      );
      setWebsocketResult(prev => {
        const next = prev.map(item =>
          String(item.id) === String(wsId)
            ? { ...item, status: 'Success', progress: 100, message: payload?.message || 'Resume processing completed.' }
            : item
        );
        maybeCallPostProcess(next);
        return next;
      });
      webSocketParserService.disconnect(wsId);
    };

    const handleFailed = (msg) => {
      const wsId = getWsId(msg);
      const payload = getWsPayload(msg);
      setResumeStatusData(prev =>
        prev.map(item =>
          String(item.id) === String(wsId)
            ? { ...item, status: 'Failed', progress: 100, message: payload?.message || payload?.error || item.apiMessage || 'Resume processing failed.' }
            : item
        )
      );
      setWebsocketResult(prev => {
        const next = prev.map(item =>
          String(item.id) === String(wsId)
            ? { ...item, status: 'Failed', progress: 100, message: payload?.message || payload?.error || item.apiMessage || 'Resume processing failed.' }
            : item
        );
        maybeCallPostProcess(next);
        return next;
      });
      webSocketParserService.disconnect(wsId);
    };

    const handleError = (msg) => {
      const wsId = getWsId(msg);
      const payload = getWsPayload(msg);
      setResumeStatusData(prev =>
        prev.map(item =>
          String(item.id) === String(wsId)
            ? { ...item, status: 'Error', progress: 100, error: (payload?.error || 'Unknown error'), message: payload?.message || payload?.error || item.apiMessage || 'Resume processing failed.' }
            : item
        )
      );
      setWebsocketResult(prev => {
        const next = prev.map(item =>
          String(item.id) === String(wsId)
            ? { ...item, status: 'Error', progress: 100, error: (payload?.error || 'Unknown error'), message: payload?.message || payload?.error || item.apiMessage || 'Resume processing failed.' }
            : item
        );
        maybeCallPostProcess(next);
        return next;
      });
      webSocketParserService.disconnect(wsId);
    };

    const maybeCallPostProcess = (resultData) => {
      if (!isMounted) return;
      const validResumeIds = resultData
        .map((item) => item.id)
        .filter((id) => Number.isFinite(Number(id)))
        .map((id) => Number(id));
      if (
        validResumeIds.length > 0 &&
        resultData.every(item =>
          item.status === 'Complete'
          || item.status === 'Failed'
          || item.status === 'Success'
          || item.status === 'Error'
        )
      ) {
        if (!isCallingPostProcessRef.current) {
          callBulkUploadPostProcess(validResumeIds, uploadIdToFileNameRef.current);
        }
      }
    };
    const unsubProgress = webSocketParserService.on("progress", handleProgress);
    const unsubSuccess = webSocketParserService.on("success", handleSuccess);
    const unsubFailed = webSocketParserService.on("failed", handleFailed);
    const unsubComplete = webSocketParserService.on("complete", handleComplete);
    const unsubError = webSocketParserService.on("error", (err) => console.error(err));

    return () => {
      unsubProgress();
      unsubSuccess();
      unsubFailed();
      unsubComplete();
      unsubError();
    };
  }, []);

  const refreshUploadedByMeList = useCallback(async () => {
    console.log("Refreshing Uploaded By Me list...");
    setShowSearchResults(false);
    setCandidates([]);
    setPage(0);
    setHasMore(true);
    if (activeTab !== "UploadedByMe") {
      setActiveTab("UploadedByMe");
      return;
    }
    await fetchCandidates(0, "UploadedByMe");
  }, [activeTab, fetchCandidates]);

  const handleModalClose = () => {
    setShowUploadModal(false);
    setUploadError('');
    setUploadSuccess('');
    setSelectedFiles([]);
    setFailedFiles([]);
    setSkippedFiles([]);
    setDragActive(false);
    setWebsocketResult([]);
    setPostProcessTable([]);
    setPostProcessError('');
    setShowOnlyPostProcessTable(false);
  };

  const handleResumeStatusClose = () => {
    webSocketParserService.disconnectAll();
    setShowResumeStatusModal(false);
    // switch tab to Uploaded By Me if there are successful uploads, otherwise stay on the same tab
    postProcessTable.some(item => item?.status === 'Complete') ? setActiveTab('UploadedByMe') : setActiveTab('Candidates');
    setPostProcessTable([]);
    setPostProcessError([]);
    setShowOnlyPostProcessTable(false);

  };

  const renderFieldsCell = (fields) => {
    if (!fields || !fields.length) return <span className="text-gray-333 text-xs">Nil</span>;
    return (
      <ul className="list-disc text-xs">
        {fields.map(field => (
          <li key={field} className="truncate">{field}</li>
        ))}
      </ul>
    );
  };


  const handleSelectionChange = (
    candidateId,
    email,
    selected,
    isEmailSent = null,
    emailSentBy = null,
    emailSentByFirstName = null,
    emailSentByLastName = null,
    emailSentByEmail = null,
  ) => {
    try {
      setSelectedIdsForEmail(prev => {
        if (selected) {
          // Add new entry if not already present
          if (!prev.some(item => item.id === candidateId)) {
            return [
              ...prev,
              {
                id: candidateId,
                email: email,
                emailSentOn: isEmailSent,
                emailSentBy,
                emailSentByFirstName,
                emailSentByLastName,
                emailSentByEmail,
              },
            ];
          }
          return prev;
        } else {
          // Remove entry
          return prev.filter(item => item.id !== candidateId);
        }
      });
    } catch (error) {
      console.error("Error in handleSelectionChange:", error);
    }
  };


  // emailType will be 'generic' for candidate checkbox toggles
  // you can store/use it as needed; for bulk email you can default to 'generic'

  const handleCloseEmail = () => {
    setShowEmailModal(false);
    setEmailSent(false);
    setEmailError("");
  };

  const handleOpenEmail = (e) => {
    e.stopPropagation();
    setShowEmailModal(true);
    setEmailSent(false);
    setEmailError("");
  };

  const handleSendEmail = async (body) => {
    setSendingEmail(true);
    setEmailError('');
    try {
      let payload = {
        candidate_ids: body.candidate_id,
        subject: body.subject,
        email_body: body.email_body,
        sender_email:user.email
      }
      const response = await RecruiterApi.sendSearchMail(payload)
      console.log(response);

      if (response.success) {
        if (response.data) {
          const sentAt = response?.data?.email_sent_at || null;
          setSelectedIdsForEmail([]);
          setCandidates((prev) =>
            prev.map((candidate) =>
              body.candidate_id.includes(candidate.id)
                ? {
                  ...candidate,
                  is_email_sent: true,
                  email_sent_at: sentAt,
                }
                : candidate
            )
          );
          toast.success(response.message || 'Email sent successfully');
        }
      } else {
        toast.warning(response.message || 'Failed to send email. Please try again.');
      }
      setEmailSent(true);
    } catch (err) {
      setEmailError('Failed to send email. Please try again.');
    } finally {
      setShowEmailModal(false);
      setSendingEmail(false);
    }
  };

  const sendPlacedFeedback = async () => {
    if (!selectedIdsForEmail.length) return;

    try {
      setSendingEmail(true);

      const payload = {
        candidate_ids: selectedIdsForEmail.map(c => c.id),
        user_payload: {
          user_id: user.id,
          email: user.email,
          request_id: generateRequestId(),
        },
        sender_email:user.email// can be dynamic based on logged in user
      };

      const response = await RecruiterApi.sendFeedBackForm(payload);

      if (response.success) {
        toast.success("Feedback form sent successfully!");
        // Remove candidates that have just been sent feedback form
        setCandidates(prev =>
          prev.filter(c =>
            !payload.candidate_ids.includes(c.id)
          )
        );
        setSelectedIdsForEmail([]);
      } else {
        toast.warning(response.message || "Failed to send feedback form.");
      }

    } catch (error) {
      console.error(error);
      toast.error("Failed to send feedback form.");
    } finally {
      setSendingEmail(false);
    }
  };

  const uploadStatusesToShow = websocketResult.filter(
    item =>
      item.status === 'Connecting'
      || item.status === 'In Progress'
      || item.status === 'Complete'
      || item.status === 'Failed'
      || item.status === 'Success'
      || item.status === 'Error'
  );

  const getUploadStatusText = (status, item) => {
    if (item?.canCancel === false) return '';
    if (status === 'Connecting') return 'Starting';
    if (status === 'In Progress') return 'Processing';
    if (status === 'Complete' || status === 'Success') return 'Processing';
    if (status === 'Failed' || status === 'Error') return 'Failed';
    return 'Processing';
  };

  const getUploadMessageText = (item) => {
    if ((item?.status === 'Failed' || item?.status === 'Error') && item?.apiMessage) return item.apiMessage;
    if (item?.message) return item.message;
    if (item?.status === 'Connecting') return 'Starting upload.';
    if (item?.status === 'In Progress') return 'Resume is processing.';
    if (item?.status === 'Complete' || item?.status === 'Success') return 'Resume is processing.';
    if (item?.status === 'Failed' || item?.status === 'Error') return 'Resume upload failed.';
    return 'Resume is processing.';
  };

  const finalUploadTableRows = (() => {
    const baseRows = Array.isArray(postProcessTable) ? [...postProcessTable] : [];
    const existingKeys = new Set(
      baseRows.map((item) => `${item?.id ?? ''}-${normalizeComparableFileName(item?.fileName || '')}`)
    );
    const failedWebsocketRows = (Array.isArray(websocketResult) ? websocketResult : [])
      .filter((item) => item?.status === 'Failed' || item?.status === 'Error')
      .map((item) => ({
        id: item?.id || '',
        fileName: item?.fileName || uploadIdToFileNameRef.current[item?.id] || '',
        status: 'Failed',
        fields: [],
        error: item?.message || item?.error || item?.apiMessage || 'Resume upload failed.',
      }))
      .filter(
        (item) =>
          !existingKeys.has(`${item?.id ?? ''}-${normalizeComparableFileName(item?.fileName || '')}`)
      );
    return [...failedWebsocketRows, ...baseRows];
  })();


const cancelUpload = async (id) => {
  try {
    console.log("cancelling upload for id:", id);
    let payload = {
      user_payload: {
        user_id: user.id,
        request_id: generateRequestId(),
        email: user.email,
        tenant: "GBB"
      },
      doc_id: id
    };
    const response = await RecruiterApi.cancelUpload(payload);
    console.log(response);

    if (response.success) {
      // Remove the item from websocketResult
      setWebsocketResult(prevResult => {
        const updatedResult = prevResult.filter(item => item.id !== id);
        
        // Check if uploadStatusesToShow would be empty after filtering
        const remainingStatuses = updatedResult.filter(
          item =>
            item.status === 'Connecting' ||
            item.status === 'In Progress' ||
            item.status === 'Complete' ||
            item.status === 'Failed' ||
            item.status === 'Success' ||
            item.status === 'Error'
        );
        
        if (remainingStatuses.length === 0) {
          handleResumeStatusClose();
        }
        
        return updatedResult;
      });
      toast.success('Upload cancelled successfully');
    } else {
      toast.warning(response.message || 'Failed to cancel upload. Please try again.');
    }
  } catch (error) {
    console.log(error);
    toast.error('An error occurred while cancelling the upload');
  }
};
  return (
    <main className="bg-white p-6 rounded-2xl shadow-sm h-full flex flex-col">
      <div className="flex-shrink-0">
        <div className="flex justify-between items-center">
          {pageType !== "matching" && !isTyping ? (
            // Original horizontal tabs for matching page
            <div className="flex gap-6">
              {[
                {
                  key: "Candidates",
                  label: "Candidates",
                  count: candidateCount,
                },
                {
                  key: "UploadedByMe",
                  label: "Uploaded by Me",
                  count: uploadedCount,
                },
                {
                  key: "Placed",
                  label: "Consultant Feedback",
                  count: placedCount,
                },
                {
                  key: "ContactedByMe",
                  label: "Contacted by Me",
                  count: contactedCount,
                },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => {
                    setSelectedIdsForEmail([]);
                    setActiveTab(tab.key);
                  }}
                  className={`relative pb-2  text-sm  font-semibold transition-colors ${
                    activeTab === tab.key ? "mph-primary" : "text-gray-666"
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.key && (
                    <span className="ml-2 bg-gray-100 mph-primary text-xs font-bold px-2 py-0.5 rounded-full">
                      {loading && candidates.length === 0
                        ? "..."
                        : searchCandidates.query?.title?.length > 0
                          ? candidates.length
                          : totalCount}
                    </span>
                  )}
                  {activeTab === tab.key && (
                    <div className="absolute left-0 right-0 bottom-0 h-0.5 bg-emerald-700 rounded-full" />
                  )}
                </button>
              ))}
            </div>
          ) : (
            // Dropdown for non-matching pages
            <div className="relative" ref={tabDropdownRef}>
              <button
                onClick={() => setShowTabDropdown(!showTabDropdown)}
                className="flex ifg items-center gap-2 text-xs  font-semibold mph-primary bg-gray-50 px-3 py-2 rounded-lg border border-gray-200 hover:shadow-sm transition-all"
              >
                <span className="flex items-center">
                  {(() => {
                    const tab = [
                      { key: "Candidates", label: "Candidates" },
                      { key: "UploadedByMe", label: "Uploaded by Me" },
                      { key: "Placed", label: "Placed Candidates" },
                      { key: "ContactedByMe", label: "Contacted by Me" },
                    ].find((tab) => tab.key === activeTab);

                    return tab?.label || "Candidates";
                  })()}

                  <span className="ml-2 bg-gray-100 mph-primary text-xs font-bold px-2 py-0.5 rounded-full">
                    {loading && candidates.length === 0 ? "..." : totalCount}
                  </span>
                </span>

                <svg
                  className={`w-4 h-4 transition-transform ${
                    showTabDropdown ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {showTabDropdown && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10 animate-fadeIn">
                  {[
                    { key: "Candidates", label: "Candidates" },
                    { key: "UploadedByMe", label: "Uploaded by Me" },
                    { key: "Placed", label: "Placed Candidates" },
                    { key: "ContactedByMe", label: "Contacted by Me" },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => {
                        setSelectedIdsForEmail([]);
                        setActiveTab(tab.key);
                        setShowTabDropdown(false);
                      }}
                      className={`w-full  jj text-left px-4 py-2 text-xs  hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg transition-colors flex justify-between items-center ${
                        tab.key === activeTab ? "bg-gray-100" : ""
                      }`}
                    >
                      <span>{tab.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2 items-center">
            {selectedIdsForEmail.length > 0 && activeTab !== "Placed" && (
              <div>
                <button
                  title="Reset"
                  onClick={() => setSelectedIdsForEmail([])}
                  className="flex items-center gap-2 bg-gradient text-white text-xs font-semibold px-4 py-2 rounded-full hover:shadow-lg transition-shadow"
                >
                  <RotateCcw size={14} />
                </button>
              </div>
            )}

            {selectedIdsForEmail.length > 0 && (
              <div>
                <button
                  onClick={(e) => {
                    if (activeTab === "Placed") {
                      sendPlacedFeedback();
                    } else {
                      handleOpenEmail(e);
                    }
                  }}
                  title={
                    activeTab === "Placed"
                      ? `Send feedback to ${selectedIdsForEmail.length} candidate(s)`
                      : `Send email to ${selectedIdsForEmail.length} candidate(s)`
                  }
                  className="flex items-center gap-2 bg-gradient text-white text-xs font-semibold px-4 py-2 rounded-full hover:shadow-lg transition-shadow"
                  disabled={!selectedIdsForEmail.length}
                >
                  <FiMail size={12} />

                  {!isTyping &&
                    (activeTab === "Placed"
                      ? `Send Feedback (${selectedIdsForEmail.length})`
                      : `Send Email (${selectedIdsForEmail.length})`)}
                </button>
              </div>
            )}

            {activeTab === "Placed" && candidates.lenght > 0 && (
              <button
                onClick={() => {
                  if (
                    selectedIdsForEmail.length ===
                    candidates.filter((c) => !c.feedback_mail).length
                  ) {
                    setSelectedIdsForEmail([]);
                  } else {
                    const all = candidates
                      .filter((c) => !c.feedback_mail)
                      .map((c) => ({
                        id: c.id,
                        email: c.email,
                        emailSentOn: c.feedback_mail,
                      }));
                    setSelectedIdsForEmail(all);
                  }
                }}
                className="flex items-center gap-2 bg-gradient text-white text-xs font-semibold px-4 py-2 rounded-full"
              >
                {selectedIdsForEmail.length ===
                candidates?.filter((c) => !c.feedback_mail).length
                  ? "Deselect All"
                  : "Select All"}
              </button>
            )}

            {/* Your Add Candidate / Get Feedback button */}
            {activeTab !== "Placed" && (
              <button
                className={`flex items-center gap-2 bg-gradient text-white text-xs font-semibold px-4 py-2 rounded-full hover:shadow-lg transition-shadow
      ${selectedIdsForEmail.length > 0 ? "opacity-70" : ""}`}
                onClick={handleAddCandidateClick}
                title="Add Candidate"
                disabled={uploading || selectedIdsForEmail.length > 0}
              >
                <FiUserPlus />
                {!isTyping && <span>Add Candidate</span>}
              </button>
            )}
          </div>
        </div>
        {pageType === "matching" && (
          <div className="flex justify-between items-center mt-4">
            <div className="flex items-center gap-1.5">
              <input type="checkbox" className="h-3.5 w-3.5 rounded" />
              <label className="text-sm font-semibold text-gray-333">
                Select all
              </label>
            </div>
            <div className="flex items-center gap-2">
              <button
                className={`${iconButtonStyles} mph-primary hover:text-green-600`}
              >
                <FiMail size={16} />
              </button>
              <button
                className={`${iconButtonStyles} text-green-500 hover:text-green-600`}
              >
                <FaWhatsapp size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      <div
        className="flex-1 overflow-y-auto pt-3 pr-2 space-y-4 pl-4"
        ref={scrollContainerRef}
        style={{ minHeight: 0 }}
      >
        {(!Array.isArray(candidates) || candidates.length === 0) && !loading ? (
          <div className="text-center text-gray-666 py-8">
            No candidates found.
          </div>
        ) : (
          (Array.isArray(candidates) ? candidates : [])
            .filter((candidate) => {
              const status = (candidate?.crm_status || "").toLowerCase();
              return (
                !status.includes("incomplete") &&
                !status.includes("blacklisted")
              );
            })
            .map((candidate, idx) => {
              const key =
                candidate.id != null
                  ? `${candidate.id}-${idx}`
                  : `candidate-${idx}`;

              return activeTab === "Placed" ? (
                <PlacedCandidateItem
                  key={key}
                  candidate={candidate}
                  selectedIds={selectedIdsForEmail}
                  onSelectionChange={handleSelectionChange}
                  showCheckbox={true}
                />
              ) : (
                <CandidateItem
                  key={key}
                  selectedIds={selectedIdsForEmail}
                  candidate={candidate}
                  pageType={pageType}
                  query={query}
                  searchType={searchType}
                  job_id={job_id}
                  isTyping={isTyping}
                  onSelectionChange={handleSelectionChange}
                  onCandidateUpdateFilter={onCandidateUpdateFilter}
                  onMoveToUploadedTab={refreshUploadedByMeList}
                />
              );
            })
        )}
        {loading && (
          <div className="text-center py-4">
            <Loader message="Loading candidates" />
          </div>
        )}
        {((!hasMore && !showSearchResults) ||
          (!searchHasMore && showSearchResults)) &&
          candidates.length > 0 && (
            <div className="text-center text-sm text-gray-999 py-2">
              No more candidates to load!
            </div>
          )}
      </div>

      {/* Bulk Resume Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 anim-slideDown">
          <div className="w-full max-w-xl text-center bg-white rounded-xl shadow-lg p-0 relative flex flex-col max-h-[90vh]">
            {/* <button
              className="absolute top-2 right-4 text-gray-400 hover:text-gray-700 text-2xl"
              onClick={handleModalClose}
              disabled={uploading}
              aria-label="Close"
            >
              &times;
            </button> */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              {/* <div className="w-full max-w-md text-center px-8 pt-8">
                <h2 className="text-md font-bold text-gray-333 mb-1">
                  Bulk Resume Upload
                </h2>
                <p className="text-xs text-gray-600 mb-2">
                  Upload up to{" "}
                  <span className="font-bold text-gray-800">5 resumes</span>{" "}
                  (PDF, DOCX, TXT, max 2MB).
                </p>
        
              </div> */}
              <div
                className={`mt-6 border-2 border-dashed border-gray-999 rounded-xl p-2 w-full mx-auto ${
                  dragActive ? "bg-emerald-50 border-emerald-400" : ""
                }`}
                style={{ maxWidth: 530 }}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="flex flex-col items-center">
                  <div className="flex flex-col items-center w-full">
                    <FiUploadCloud className="mx-auto h-12 w-12 text-gray-300" />
                    <p className="mt-2 text-sm font-semibold text-gray-666">
                      Drag and drop your resume here
                    </p>
                  </div>
                  <div className="flex items-center justify-center my-2 w-full">
                    <div className="w-24 border-t border-gray-300"></div>
                    <span className="flex-shrink-0 mx-6 text-gray-666 text-xs">
                      Or
                    </span>
                    <div className="w-24 border-t border-gray-300"></div>
                  </div>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleFileChange}
                    disabled={uploading}
                    className="hidden"
                    id="bulk-resume-upload-input"
                  />
                  <label
                    htmlFor="bulk-resume-upload-input"
                    className="profiler-bg text-xs font-semibold px-3 py-1.5 rounded-full flex items-center justify-center gap-2 mx-auto cursor-pointer"
                    style={{ marginBottom: 0 }}
                  >
                    Browse resumes
                  </label>
                <div className="mt-3 text-xs text-center w-full text-gray-600 space-y-1">
                  <p>
                    Upload up to 5 resumes (PDF or DOCX, max 2MB each)
                  </p>
                  {/* <p>
                      Supported formats:{" "}
                      <span className="font-semibold text-gray-800">
                        PDF, DOCX
                      </span>
                    </p>

                    <p>
                      Maximum file size:{" "}
                      <span className="font-semibold text-gray-800">
                        2MB per file
                      </span>
                    </p>

                    <p>
                      You can upload up to{" "}
                      <span className="font-semibold text-gray-800">
                        5 resumes
                      </span>
                    </p> */}
                  </div>
                  <div className="mt-2 text-xs text-left w-full overflow-y-auto space-y-2">
                    {selectedFiles.length > 0 && (
                      <div className=" overflow-y-auto">
                        {/* <div className="font-semibold mb-1 text-center text-xs mph-primary">Ready to upload:</div> */}
                        <div className="space-y-1.5">
                          {selectedFiles.map((file, idx) => (
                            <div
                              key={`${file.name}-${idx}`}
                              className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md border border-emerald-200"
                            >
                              <span className="text-emerald-800 text-xs truncate font-medium">{file.name}</span>
                              <button
                                type="button"
                                className="text-emerald-700 hover:text-red-600 text-xl leading-none"
                                onClick={() => handleRemoveSelectedFile(file, idx)}
                                disabled={uploading}
                                aria-label={`Remove ${file.name}`}
                                title="Remove file"
                              >
                                &times;
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {skippedFiles.length > 0 && (
                      <div className="overflow-y-auto">
                        {/* <div className="font-semibold mb-1 text-center text-xs text-red-600">Skipped files:</div> */}
                        <div className="space-y-1.5">
                          {skippedFiles.map((file, idx) => (
                            <div
                              key={`${file.name}-${file.reason}-${idx}`}
                              className="px-2 py-1.5 rounded-md border border-amber-200"
                            >
                              <div className="text-xs font-semibold text-amber-900 break-all">
                                {file.name}
                              </div>
                              <div className="text-xs text-amber-700 mt-0.5 break-words">
                                {file.reason}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {failedFiles.length > 0 && (
                      <div className="text-red-600 max-h-24 overflow-y-auto">
                        <div className="font-semibold mb-1">Upload failed:</div>
                        {failedFiles.map((file, idx) => (
                          <div key={`${file.fileName || file.name || idx}-${idx}`} className="break-words">
                            <span className="font-semibold">{file.fileName || file.name || 'Unknown file'}</span>
                            {`: ${file.message || file.error || 'Failed to upload.'}`}
                          </div>
                        ))}
                      </div>
                    )}

                    {uploadError && <div className="text-red-500 text-center text-xs">{uploadError}</div>}
                    {uploadSuccess && <div className="mph-primary">{uploadSuccess}</div>}
                  </div>
                </div>
              </div>

                      {/* <div className="mx-auto mt-1 py-1 px-6 w-full">
                  <label className="block text-xs text-gray-999  text-left">
                    Sector <span className="text-red-500">*</span>
                  </label>
                  <MultiSelectCandidateDropdown
                    label=""
                    name="sectors"
                    options={sectors.data || []}
                    selected={selectedSectors}
                    onChange={(selected) => setSelectedSectors(selected)}
                    isMultiSelect={true}
                    isLoading={sectors.loading}
                    placeholder="Select sectors..."
                  />
                </div> */}
            </div>
            <div className="flex justify-end gap-2 mt-4 px-8 pb-6">
              <button
                className="px-4 py-1  rounded-full bg-gray-200 text-gray-333 text-xs"
                onClick={handleModalClose}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                className="px-4 py-1  rounded-full bg-gradient text-white text-xs font-semibold disabled:opacity-50"
                onClick={handleUpload}
                disabled={uploading || selectedFiles.length === 0}
              >
                {uploading ? "Uploading..." : "Upload"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showResumeStatusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 anim-slideDown">
          <div className="w-1/2 max-h-[calc(100vh-7rem)] bg-white rounded-2xl shadow-lg flex flex-col relative p-6">
            <button
              className="absolute top-2 right-4 text-gray-666 hover:text-gray-700 text-2xl"
              onClick={() => {
                webSocketParserService.disconnectAll();
                setShowResumeStatusModal(false);
                setPostProcessTable([]);
                setPostProcessError("");
                setShowOnlyPostProcessTable(false);
                handleResumeStatusClose();
                refreshUploadedByMeList();
              }}
            >
              &times;
            </button>

            <p className="text-sm font-semibold mph-primary mb-4">
              Uploading Your Resume(s)...
            </p>

            {/* Only show the websocket status if the postprocess table is NOT shown */}
            {!showOnlyPostProcessTable && (
              <div className="space-y-3 overflow-y-auto">
                {uploadStatusesToShow.map((item, idx) => (
                  <div className="flex gap-2  " key={item.id}>
                    <div
                      key={item.id}
                      className="border w-full rounded-lg p-3 shadow-sm"
                    >
                      <div className="flex justify-between items-center text-xs">
                        <div className="max-w-[75%]">
                          <div className="font-medium text-gray-700 truncate">
                            {item.fileName || item.id}
                          </div>
                          <div className={`mt-1 text-[11px] ${item.status === "Failed" || item.status === "Error" ? "text-red-600" : "text-gray-500"}`}>
                            {getUploadMessageText(item)}
                          </div>
                        </div>
                        <span
                          style={{
                            minWidth: 75,
                            textAlign: "right",
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          {(item.status === "Connecting" || item.status === "In Progress" || item.status === "Complete" || item.status === "Success") && (
                            <span className="inline-block align-middle mr-1">
                              <svg
                                className="animate-spin h-4 w-4 mph-primary"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                            </span>
                          )}
                          {getUploadStatusText(item.status, item)}
                        </span>
                      </div>
                    </div>

                    {(item.canCancel !== false) &&
                      item.status !== "Complete" &&
                      item.status !== "Success" &&
                      item.status !== "Failed" &&
                      item.status !== "Error" && (
                        <div
                          onClick={() => cancelUpload(item.id)}
                          title="cancel"
                          className="flex rounded cursor-pointer p-2 items-center justify-center"
                        >
                          <CircleX className="h-5 w-5 text-gray-500" />
                        </div>
                      )}
                  </div>
                ))}
              </div>
            )}

            {/* Show post-process table if available */}
            {showOnlyPostProcessTable && finalUploadTableRows.length > 0 && (
              <div className="w-full text-left anim-slideUp">
                {/* Show missing fields warning */}
                {(() => {
                  const criticalFields = new Set();
                  finalUploadTableRows.forEach((item) => {
                    item.fields?.forEach((field) => {
                      const fieldLower = field.toLowerCase();
                      if (["first_name", "email"].includes(fieldLower)) {
                        criticalFields.add(field);
                      }
                    });
                  });

                  if (criticalFields.size > 0) {
                    return (
                      <div className="rounded text-blue-800 flex items-start gap-2 text-xs font-normal shadow-none mb-2">
                        <svg
                          className="w-4 h-4 flex-shrink-0 text-blue-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span>
                          Resumes with missing required field(s) (
                          <span className="font-bold text-blue-900">
                            {Array.from(criticalFields).join(", ")}
                          </span>
                          ){" "}
                          <span className="font-bold">must be re-uploaded</span>{" "}
                          with the required details to proceed with
                          shortlisting.
                        </span>
                      </div>
                    );
                  }
                  return null;
                })()}
                <div className="mt-2 w-full overflow-x-auto max-h-[calc(100vh-17rem)] overflow-y-auto">
                    <table className="min-w-full border rounded">
                      <thead>
                        <tr className="bg-gray-100 text-xs">
                          <th className="px-3 py-2 border">File Name</th>
                          <th className="px-3 py-2 border">Message</th>
                          {finalUploadTableRows.some(
                            (item) => item.fields?.length > 0,
                          ) && (
                            <th className="px-3 py-2 border">Missing Fields</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {finalUploadTableRows.map((item) => {
                          let displayStatus = "";
                          let statusClass = "";
                          if (item.error) {
                            displayStatus = "Failed";
                            statusClass = "text-red-700";
                          } else if (
                            item.fields &&
                            Array.isArray(item.fields) &&
                            item.fields.length > 0
                          ) {
                            displayStatus = "Pending Verification";
                            statusClass = "text-amber-600";
                          } else {
                            displayStatus = "Upload completed successfully";
                            statusClass = "text-green-600";
                          }
                          return (
                            <tr key={item.id}>
                              <td className="px-3 py-2 border text-xs font-medium">
                                {item.fileName}
                              </td>
                              
                              <td
                                className={`px-3 py-2 border text-xs ${
                                  item.error ? "text-red-700" : "text-gray-333"
                                }`}
                              >
                                {item.error || item.message || (item.fields?.length > 0 ? "Missing mandatory fields found." : "Upload completed successfully.")}
                              </td>
                              {item?.fields &&
                                Array.isArray(item.fields) &&
                                item.fields.length > 0 && (
                                  <td className="px-3 py-2 border">
                                    {renderFieldsCell(item.fields)}
                                  </td>
                                )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                </div>

              </div>
            )}
            {/* {postProcessError && <div className="mt-2 text-danger text-xs">{postProcessError}</div>} */}

            {/* <div className="flex justify-end mt-4">
              <button
                className="px-4 py-2 rounded-2xl profiler-bg text-white text-xs font-semibold shadow"
                onClick={() => {
                  handleResumeStatusClose();
                }}
              >
                Close
              </button>
            </div> */}
          </div>
        </div>
      )}

      {showEmailModal && (
        <EmailModal
          isOpen={showEmailModal}
          onClose={handleCloseEmail}
          candidateId={selectedIdsForEmail.map((candidate) => candidate.id)}
          candidateEmail={selectedIdsForEmail.map(
            (candidate) => candidate.email,
          )}
          // isEmailSent={candidateData?.is_email_sent || false}
          // emailSentOn={candidateData?.email_sent_at || ""}
          onSend={(data) => {
            handleSendEmail(data);
          }}
          type={"generic"}
          alreadySentCandidates={selectedIdsForEmail.filter(
            (candidate) => candidate?.emailSentOn,
          )}
          emailSentBy={
            selectedIdsForEmail.find((candidate) => candidate?.emailSentOn)
              ?.emailSentBy ||
            selectedIdsForEmail[0]?.emailSentBy ||
            null
          }
          emailSentByFirstName={
            selectedIdsForEmail.find((candidate) => candidate?.emailSentOn)
              ?.emailSentByFirstName ||
            selectedIdsForEmail[0]?.emailSentByFirstName ||
            null
          }
          emailSentByLastName={
            selectedIdsForEmail.find((candidate) => candidate?.emailSentOn)
              ?.emailSentByLastName ||
            selectedIdsForEmail[0]?.emailSentByLastName ||
            null
          }
          emailSentByEmail={
            selectedIdsForEmail.find((candidate) => candidate?.emailSentOn)
              ?.emailSentByEmail ||
            selectedIdsForEmail[0]?.emailSentByEmail ||
            null
          }
          isEmailSent={selectedIdsForEmail.some(
            (candidate) => candidate.emailSentOn,
          )}
          sending={sendingEmail}
          sent={emailSent}
          error={emailError}
        />
      )}
    </main>
  );
};

export default CandidateList;
